import { isSupportedImageFile } from '../../utils/imageHelper';

export const REVIEW_FIELDS = [
  'invoice_number',
  'display_name',
  'rating',
  'review_text',
];

export const INITIAL_VALUES = {
  invoiceNumber: '',
  displayName: '',
  rating: 0,
  reviewText: '',
};

export const MAX_REVIEW_PHOTOS = 3;
export const MAX_REVIEW_PHOTO_SIZE = 5 * 1024 * 1024;
export const INVALID_REVIEW_PHOTO_TYPE = 'Image must be a JPEG, PNG, or WebP file';
export const INVALID_REVIEW_PHOTO_SIZE = 'Image may not be larger than 5MB';
export const DEFAULT_REVIEW_PHOTO_ERROR = 'Could not upload this photo, please try again.';
const UNUSABLE_API_ERROR_MESSAGE = 'I got something undefined errors';

export const getResponseStatus = error => {
  if (!error || !error.response) return null;
  if (error.response.status) return error.response.status;

  const responseData = error.response.data;
  const apiError = responseData && responseData.error;
  return apiError && apiError.status_code ? apiError.status_code : null;
};

export const getApiError = error => {
  const responseData = error && error.response && error.response.data;
  return responseData && responseData.error ? responseData.error : null;
};

export const getImageValidationMessages = apiError => {
  const sourceErrors = apiError && apiError.errors;
  if (!sourceErrors || typeof sourceErrors !== 'object' || Array.isArray(sourceErrors)) {
    return [];
  }

  const messages = Object.keys(sourceErrors).reduce((result, field) => {
    if (field !== 'images' && !field.startsWith('images.')) return result;

    const fieldMessages = Array.isArray(sourceErrors[field])
      ? sourceErrors[field]
      : [sourceErrors[field]];
    return [
      ...result,
      ...fieldMessages.filter(message => typeof message === 'string'),
    ];
  }, []);

  return [...new Set(messages)];
};

export const getUploadedImageUrl = response => {
  const responseBody = response && response.data;
  const data = responseBody && responseBody.data;
  return data && typeof data.image_url === 'string' && data.image_url
    ? data.image_url
    : null;
};

export const getPhotoValidationError = file => {
  if (!isSupportedImageFile(file)) {
    return INVALID_REVIEW_PHOTO_TYPE;
  }
  if (file.size > MAX_REVIEW_PHOTO_SIZE) return INVALID_REVIEW_PHOTO_SIZE;
  return '';
};

export const getPhotoUploadError = error => {
  const status = getResponseStatus(error);
  if (status === 404) return 'This order is no longer eligible for a review.';
  if (status === 429) return 'Too many uploads. Wait a moment, then retry.';

  if (status === 422) {
    const apiError = getApiError(error);
    const message = apiError && typeof apiError.message === 'string'
      ? apiError.message
      : '';
    const normalizedMessage = message.trim();
    if (
      normalizedMessage
      && normalizedMessage !== UNUSABLE_API_ERROR_MESSAGE
    ) return message;
  }

  return DEFAULT_REVIEW_PHOTO_ERROR;
};

export const normalizeValidationError = apiError => {
  const sourceErrors = apiError && apiError.errors;
  if (!sourceErrors || typeof sourceErrors !== 'object' || Array.isArray(sourceErrors)) {
    return null;
  }

  const errors = {};
  REVIEW_FIELDS.forEach(field => {
    const messages = sourceErrors[field];
    if (Array.isArray(messages)) {
      const stringMessages = messages.filter(message => typeof message === 'string');
      if (stringMessages.length > 0) errors[field] = stringMessages;
    }
  });

  return Object.keys(errors).length > 0 ? { errors } : null;
};

export const clearValidationField = (validationError, fieldToClear) => {
  if (!validationError || !validationError.errors) return validationError;

  const errors = {};
  REVIEW_FIELDS.forEach(field => {
    if (field !== fieldToClear && validationError.errors[field]) {
      errors[field] = validationError.errors[field];
    }
  });

  return Object.keys(errors).length > 0 ? { errors } : null;
};

export const getOrderFromResponse = response => {
  const responseBody = response && response.data;
  const paginator = responseBody && responseBody.data;
  const sales = paginator && Array.isArray(paginator.data) ? paginator.data : [];
  return sales.length > 0 ? sales[0] : null;
};

export const validateDisplayName = value => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';
  if (/\s/.test(trimmedValue)) return 'Display name must be a single word';
  if (trimmedValue.length < 3 || trimmedValue.length > 50) {
    return 'Display name must be between 3 and 50 characters';
  }
  return '';
};

export const getMaskedDisplayName = value => {
  const trimmedValue = value.trim();
  if (validateDisplayName(trimmedValue) || !trimmedValue) return '';
  const characters = Array.from(trimmedValue);
  return `${characters[0]}***${characters[characters.length - 1]}`;
};

export const textOrDash = value => (value == null || value === '' ? '—' : value);
